/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {monthlySalaryGetAll, monthlySalaryExcel} from '../../../redux/slices/staff.slice'
import {basicLookup, projectLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

import {Years} from '../../../helpers'

const selectStaffData = (state) => ({
  monthlySalary: state.staff.monthlySalary,
  loading: state.staff.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  Month: state.lookup.Month
})

function List() {
  const intl = useIntl()
  const {monthlySalary, loading} = useSelector(selectStaffData)
  const {projectLookupList, Month} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(monthlySalaryGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(basicLookup('Month'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      monthlySalaryExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.MONTHLY_SALARY'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.MONTHLY_SALERY.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={`/staff/monthly-salary/detail?projectId=${
            props.data[props.row.index].projectId
          }&year=${props.data[props.row.index].year}&month=${props.data[props.row.index].month}`}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.MONTHLY_SALERY.SEARCH.MONTHNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'monthName',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={`/staff/monthly-salary/detail?projectId=${
            props.data[props.row.index].projectId
          }&year=${props.data[props.row.index].year}&month=${props.data[props.row.index].month}`}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.MONTHLY_SALERY.SEARCH.YEAR'})}
          className='min-w-125px'
        />
      ),
      accessor: 'year',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          to={`/staff/monthly-salary/detail?projectId=${
            props.data[props.row.index].projectId
          }&year=${props.data[props.row.index].year}&month=${props.data[props.row.index].month}`}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.MONTHLY_SALERY.SEARCH.PAYMENT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'payment',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          price
          to={`/staff/monthly-salary/detail?projectId=${
            props.data[props.row.index].projectId
          }&year=${props.data[props.row.index].year}&month=${props.data[props.row.index].month}`}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.MONTHLY_SALERY.SEARCH.BALANCE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'balance',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          price
          to={`/staff/monthly-salary/detail?projectId=${
            props.data[props.row.index].projectId
          }&year=${props.data[props.row.index].year}&month=${props.data[props.row.index].month}`}
        />
      ),
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.MONTHLY_SALERY.SEARCH.BESPAYMENT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'besPayment',
      Cell: ({...props}) => (
        <CustomCell
          item={props}
          price
          to={`/staff/monthly-salary/detail?projectId=${
            props.data[props.row.index].projectId
          }&year=${props.data[props.row.index].year}&month=${props.data[props.row.index].month}`}
        />
      ),
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'STAFF.MONTHLY_SALERY.SEARCH.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'STAFF.MONTHLY_SALERY.SEARCH.MONTHNAME',
          data: Month,
          name: 'month',
        },
        {
          title: 'STAFF.MONTHLY_SALERY.SEARCH.YEAR',
          data: Years,
          name: 'year',
        },
      ],
    },
  ], [projectLookupList, Month, handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.MONTHLY_SALARY'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'MONTHLY_SALARY'}
        />
        <KTCardBody>
          {!monthlySalary && <ListLoading />}
          {monthlySalary && (
            <Table
              items={monthlySalary}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
